<template>
  <b-form-group label-size="lg" label-class="font-weight-bold">
    <template #label>
      <div class="d-flex flex-directions-row">
        <div>{{ formLabelName }}</div>
        <b-badge :class="`authority-badge ${editAuth}`">{{ authlityBadgeLabel }}</b-badge>
      </div>
    </template>
    <div v-if="settingDescription" :class="{ 'setting-description': true, owner: isOwner }">
      {{ settingDescription }}
    </div>
    <b-form-radio-group v-if="isOwner" v-model="restrictType" stacked @input="updateValue">
      <!-- 禁止 -->
      <b-form-radio :value="0" @change="updateRadioValue">{{ omitLabelName }}</b-form-radio>
      <!-- 許可 -->
      <b-form-radio :value="1" @change="updateRadioValue">
        <div class="d-flex flex-direction-row">
          <div>{{ permitLableName }}</div>
          <div v-if="isInvalidPermit" class="invalid-checkbox-label">{{ customOptionAlert }}</div>
        </div>
      </b-form-radio>
      <b-form-checkbox-group
        v-if="isPermitCustom"
        class="file-type-checkbox"
        v-model="customFileType"
        :disabled="restrictType !== 1"
        :options="filetypes"
        @input="updateValue"
      />
      <!-- direct設定準拠 -->
      <b-form-radio :value="2" @change="updateRadioValue">{{ directLabelName }}</b-form-radio>
    </b-form-radio-group>
  </b-form-group>
</template>
<style lang="scss" scoped>
.authority-badge {
  margin: auto 0 auto auto;
  &.admin {
    background-color: #8ca6b7;
    border-color: #8ca6b7;
  }
  &.owner {
    background-color: #d56c9c;
    border-color: #d56c9c;
  }
}
.setting-description {
  white-space: pre-line;
  &.owner {
    padding: 16px 0;
  }
}
.invalid-checkbox-label {
  padding-left: 20px;
  margin-top: auto;
  font-size: 12px;
  color: #ff976a;
}
.file-type-checkbox {
  display: flex;
  flex-direction: column;
  padding-left: 1.5rem;
}
</style>
<script lang="ts">
import { computed, defineComponent, onMounted, ref, watch, type PropType } from "vue";
import { RestrictType } from "@/domain-setting-types";

type Option = {
  text: string;
  value: string;
};

const defaultFileTypes: Option[] = [
  { text: "画像", value: "image" },
  { text: "動画", value: "movie" },
  { text: "PDF", value: "pdf" },
  { text: "Office系ファイル", value: "office" },
];

const DEFAULT_DESCRIPTION = "設定の変更は、所有者へご依頼ください。";

export default defineComponent({
  name: "restrict-type-setting",
  components: {},
  props: {
    formLabelName: { type: String, required: true, default: () => "" },
    desc: { type: String, default: () => "" },
    defaultRestrictType: {
      type: Number as PropType<RestrictType>,
      required: true,
      default: () => RestrictType.omit,
    },
    defaultCustomFileType: { type: Array as PropType<string[]>, required: true, default: () => [] },
    omitLabelName: { type: String, default: () => "禁止" },
    permitLableName: { type: String, default: () => "許可" },
    directLabelName: { type: String, default: () => "directの設定に準ずる" },
    isOwner: { type: Boolean, default: () => false }, // ユーザがオーナーか
    isPermitCustom: { type: Boolean, default: () => false }, // 「許可」の詳細設定を行うか
    filetypes: { type: Array as PropType<Option[]>, default: () => defaultFileTypes },
    customOptionAlert: { type: String, default: () => "許可するファイル種類を選択してください" },
  },
  setup(props, { emit }) {
    const editAuth = ref<"owner" | "admin">("owner");
    const restrictType = ref<RestrictType>(RestrictType.omit);
    const ALL_CUSTOM_CHECKBOX_LIST = ref<string[]>(defaultFileTypes.map((option) => option.value));
    const customFileType = ref<string[]>([]);

    watch(
      () => props.filetypes,
      (): void => {
        ALL_CUSTOM_CHECKBOX_LIST.value = props.filetypes.map((option) => option.value);
      },
      { immediate: true }
    );

    // 設定権のラベル
    const authlityBadgeLabel = computed((): string => {
      return editAuth.value === "owner" ? "所有者のみ" : "管理者以上";
    });
    // 設定の説明 (管理者: 「設定の変更は、所有者へご依頼ください。」)
    const settingDescription = computed((): string => {
      return props.isOwner ? props.desc : DEFAULT_DESCRIPTION;
    });
    // 許可(permit)ラジオボタンがinvalidであるか
    const isInvalidPermit = computed((): boolean => {
      // 「許可」の詳細設定が存在 & 許可ラジオボタンを選択 & 詳細のチェックボックスが何もチェックされていない
      return props.isPermitCustom && restrictType.value === 1 && !customFileType.value.length;
    });

    onMounted(() => {
      restrictType.value = props.defaultRestrictType;
      // 「許可」の詳細設定あり & 許可選択
      if (props.isPermitCustom && restrictType.value === 1) {
        // 詳細設定にチェックが 1つ以上存在する場合: それを継承 / 0の場合: 全てにチェック
        customFileType.value = props.defaultCustomFileType.length
          ? props.defaultCustomFileType
          : ALL_CUSTOM_CHECKBOX_LIST.value;
      }
    });

    const updateRadioValue = (): void => {
      if (!props.isPermitCustom) return;
      // ラジオボタン:「許可」(permit) + カスタムが何も選択されていない時に全選択肢を選択
      if (restrictType.value === RestrictType.permit && !customFileType.value.length) {
        customFileType.value = ALL_CUSTOM_CHECKBOX_LIST.value;
      }
    };

    const updateValue = (): void => {
      emit("change", {
        restrictType: restrictType.value,
        customFileType: customFileType.value,
      });
    };

    return {
      editAuth,
      restrictType,
      ALL_CUSTOM_CHECKBOX_LIST,
      customFileType,
      authlityBadgeLabel,
      settingDescription,
      isInvalidPermit,
      updateRadioValue,
      updateValue,
    };
  },
});
</script>
